// apps/accounts/src/components/LabourDetailModal.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import pb from '../lib/pocketbaseClient';
import { X, Calendar, DollarSign, TrendingUp, TrendingDown, Clock, Plus } from 'lucide-react';
import { formatDate } from '../utils/dateHelpers';
import toast from 'react-hot-toast';
import { cn } from '../lib/utils';

export default function LabourDetailModal({ labour, open, onClose, onEdit, vendors, sites }) {
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [salaryData, setSalaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  
  // Payment form
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && labour) {
      fetchData();
    }
  }, [open, labour, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const monthStart = `${selectedMonth}-01`;
      const monthEnd = `${selectedMonth}-${new Date(parseInt(selectedMonth.split('-')[1]), parseInt(selectedMonth.split('-')[0]), 0).getDate()}`;

      // Attendance
      const attendance = await pb.collection('labour_attendance').getFullList({
        filter: `labour="${labour.id}" && date >= "${monthStart}" && date <= "${monthEnd}"`,
        sort: '-date',
      });
      setAttendanceData(attendance);

      // Calculate salary
      const present = attendance.filter(r => r.status === 'present').length;
      const halfDay = attendance.filter(r => r.status === 'halfday').length;
      const totalDays = present + (halfDay * 0.5);
      const dailyRate = labour.dailyRate || 0;
      const totalSalary = totalDays * dailyRate;

      setSalaryData({
        present,
        halfDay,
        absent: attendance.length - present - halfDay,
        totalDays,
        totalSalary,
        dailyRate,
      });

      // Transactions
      const trans = await pb.collection('labour_salary_transactions').getFullList({
        filter: `labour="${labour.id}"`,
        sort: '-date',
      });
      setTransactions(trans);
    } catch (error) {
      console.error(error);
      setError('Failed to load data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getTotalPaid = () => {
    return transactions
      .filter(t => t.type === 'payment')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  };

  const getTotalAdvance = () => {
    return transactions
      .filter(t => t.type === 'advance')
      .reduce((sum, t) => sum + (t.amount || 0), 0);
  };

  const getTotalDue = () => {
    const totalPaid = getTotalPaid();
    return (salaryData?.totalSalary || 0) - totalPaid;
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await pb.collection('labour_salary_transactions').create({
        labour: labour.id,
        amount: parseFloat(paymentAmount),
        type: 'payment',
        note: paymentNote || 'Salary payment',
        date: paymentDate,
      });
      toast.success('Payment recorded successfully');
      setPaymentAmount('');
      setPaymentNote('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      fetchData(); // refresh
    } catch (err) {
      console.error(err);
      setError('Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  // Helper: format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null || isNaN(amount)) return 'Rs.0.00';
    return `Rs.${parseFloat(amount).toFixed(2)}`;
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <div className="flex items-center gap-3">
              {labour.photo ? (
                <img
                  src={pb.files.getUrl(labour, labour.photo)}
                  alt={labour.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-amber-400"
                />
              ) : (
                <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                  <span className="text-2xl font-bold text-slate-500">{labour.name?.[0]}</span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-bold text-slate-800">{labour.name}</h2>
                <p className="text-sm text-slate-500">
                  {labour.expand?.vendor?.name || 'N/A'} � {labour.expand?.site?.name || 'N/A'}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="overview" className="space-y-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="attendance">Attendance</TabsTrigger>
              <TabsTrigger value="salary">Salary & Payments</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Daily Rate</p>
                    <p className="text-2xl font-bold text-slate-800">{formatCurrency(labour.dailyRate || 0)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Skill</p>
                    <Badge variant={labour.skill === 'skilled' ? 'success' : 'secondary'}>
                      {labour.skill || 'unskilled'}
                    </Badge>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-xs text-slate-500">Status</p>
                    <Badge variant={labour.status === 'active' ? 'success' : 'secondary'}>
                      {labour.status}
                    </Badge>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-4 bg-slate-50 p-4 rounded-lg border">
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Quick Salary Summary</h4>
                {loading ? (
                  <div className="flex items-center justify-center h-20">
                    <div className="w-6 h-6 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-xs text-slate-500">Month</p>
                      <p className="font-medium">{selectedMonth}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Working Days</p>
                      <p className="font-bold text-slate-700">{salaryData?.totalDays?.toFixed(1) || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Total Salary</p>
                      <p className="font-bold text-emerald-600">{formatCurrency(salaryData?.totalSalary)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Due</p>
                      <p className={getTotalDue() > 0 ? "font-bold text-red-600" : "font-bold text-green-600"}>
                        {getTotalDue() > 0 ? '+' : ''}{formatCurrency(getTotalDue())}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-4 flex gap-2">
                <Button onClick={() => onEdit(labour)} className="bg-amber-500 hover:bg-amber-600 text-white">
                  Edit Labour
                </Button>
                <Button variant="outline" onClick={onClose}>Close</Button>
              </div>
            </TabsContent>

            {/* Attendance Tab */}
            <TabsContent value="attendance">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-1 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
                  />
                </div>
                <Button variant="outline" size="sm" onClick={fetchData}>Refresh</Button>
              </div>

              {loading ? (
                <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Present</p><p className="text-2xl font-bold text-emerald-600">{salaryData?.present || 0}</p></CardContent></Card>
                    <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Half Day</p><p className="text-2xl font-bold text-yellow-600">{salaryData?.halfDay || 0}</p></CardContent></Card>
                    <Card><CardContent className="p-3 text-center"><p className="text-xs text-slate-500">Absent</p><p className="text-2xl font-bold text-red-600">{salaryData?.absent || 0}</p></CardContent></Card>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                          <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {attendanceData.length === 0 ? (
                          <tr><td colSpan="2" className="px-3 py-4 text-center text-slate-400">No attendance records</td></tr>
                        ) : (
                          attendanceData.map(a => (
                            <tr key={a.id}>
                              <td className="px-3 py-2">{formatDate(a.date)}</td>
                              <td className="px-3 py-2">
                                <Badge
                                  variant={a.status === 'present' ? 'success' : a.status === 'halfday' ? 'warning' : 'secondary'}
                                >
                                  {a.status}
                                </Badge>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </TabsContent>

            {/* Salary & Payments Tab */}
            <TabsContent value="salary">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="px-3 py-1 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
                  />
                  <Button variant="outline" size="sm" onClick={fetchData}>Refresh</Button>
                </div>

                {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg border border-red-200">{error}</div>}

                {loading ? (
                  <div className="flex justify-center py-8"><div className="w-8 h-8 border-4 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>
                ) : (
                  <>
                    {/* Summary */}
                    <div className="bg-gradient-to-r from-emerald-50 to-amber-50 p-4 rounded-lg border">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                        <div>
                          <p className="text-xs text-slate-500">Working Days</p>
                          <p className="text-xl font-bold text-slate-700">{salaryData?.totalDays?.toFixed(1) || 0}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Total Salary</p>
                          <p className="text-xl font-bold text-emerald-600">{formatCurrency(salaryData?.totalSalary)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Total Paid</p>
                          <p className="text-xl font-bold text-amber-600">{formatCurrency(getTotalPaid())}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Due</p>
                          <p className={getTotalDue() > 0 ? "text-xl font-bold text-red-600" : "text-xl font-bold text-green-600"}>
                            {getTotalDue() > 0 ? '+' : ''}{formatCurrency(getTotalDue())}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Form */}
                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                      <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                        <Plus className="w-4 h-4 text-amber-500" />
                        Record Payment
                      </h4>
                      <form onSubmit={handlePaymentSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Amount (Rs.)</label>
                          <Input
                            type="number"
                            step="0.01"
                            value={paymentAmount}
                            onChange={(e) => setPaymentAmount(e.target.value)}
                            placeholder="0.00"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                          <Input
                            type="date"
                            value={paymentDate}
                            onChange={(e) => setPaymentDate(e.target.value)}
                            required
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-xs font-medium text-slate-600 mb-1">Note</label>
                          <Input
                            value={paymentNote}
                            onChange={(e) => setPaymentNote(e.target.value)}
                            placeholder="Payment note..."
                          />
                        </div>
                        <div className="flex items-end">
                          <Button type="submit" disabled={submitting} className="bg-amber-500 hover:bg-amber-600 text-white w-full">
                            {submitting ? 'Saving...' : 'Record Payment'}
                          </Button>
                        </div>
                      </form>
                    </div>

                    {/* Transaction History */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Transaction History</h4>
                      <div className="max-h-48 overflow-y-auto border rounded-lg">
                        {transactions.length === 0 ? (
                          <p className="text-center text-slate-400 text-sm py-4">No transactions yet</p>
                        ) : (
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Date</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Type</th>
                                <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600">Amount</th>
                                <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600">Note</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y">
                              {transactions.map(t => (
                                <tr key={t.id} className="hover:bg-slate-50">
                                  <td className="px-3 py-2 text-xs">{formatDate(t.date)}</td>
                                  <td className="px-3 py-2">
                                    <Badge variant={t.type === 'payment' ? 'success' : 'warning'}>
                                      {t.type}
                                    </Badge>
                                  </td>
                                  <td className="px-3 py-2 text-right font-medium">
                                    {t.type === 'payment' ? '-' : '+'}{formatCurrency(t.amount)}
                                  </td>
                                  <td className="px-3 py-2 text-xs text-slate-500">{t.note || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}